import cn from "classnames";
import DefaultSelect, { components as selectComponents } from "react-select";
import AsyncSelect from "react-select/async";

import { Sizes } from "./Select.types";

import DropdownIcon from "assets/icons/dropdown-icon.svg?react";
import CrossIcon from "assets/icons/cross.svg?react";

import { Typography } from "shared/ui/components/Typography";

import styles from "./Select.module.scss";

const MODES: any = {
  default: "default",
  portal: "portal",
};

const TYPES: any = {
  default: DefaultSelect,
  async: AsyncSelect,
};

// const Control = React.memo(
//   ({ children, innerProps, menuIsOpen, innerRef, haveError, isFocused }) => {
//     return (
//       <div
//         {...innerProps}
//         ref={innerRef}
//         className={cn(styles.control, {
//           [styles.controlFocused]: isFocused,
//           [styles.controlHaveError]: haveError,
//           [styles.controlOpen]: menuIsOpen,
//         })}
//       >
//         {children}
//       </div>
//     );
//   },
// );

const Control = ({
  withoutBorder,
  controlClassName,
  isFocused,
  haveError,
  menuIsOpen,
  isMulti,
  themeCustom,
  isDisabled,
  ...restProps
}: any) => (
  <selectComponents.Control
    {...restProps}
    className={cn(
      styles.control,
      {
        [styles.controlLight]: themeCustom === "light",
        [styles.controlFocused]: isFocused,
        [styles.controlHaveError]: haveError,
        [styles.controlOpen]: menuIsOpen,
        [styles.controlOpenLight]: menuIsOpen && themeCustom === "light",
        [styles.controlMulti]: isMulti,
        [styles.controlDisabled]: isDisabled,
        [styles.controlWithoutBorder]: withoutBorder,
      },
      controlClassName
    )}
  />
);

const DropdownIndicator = () => <DropdownIcon className={styles.indicator} />;

const ClearIndicator = ({ innerProps, clearText }: any) => (
  <Typography
    {...innerProps}
    variant="bodyM"
    weight="medium"
    className={styles.clear}
  >
    {clearText || "Clear"}
  </Typography>
);

const Menu = ({ menuClassName, themeCustom, ...props }: any) => (
  <selectComponents.Menu
    {...props}
    className={cn(styles.menu, menuClassName)}
  />
);

const Placeholder = ({
  innerProps,
  searchable,
  children,
  themeCustom,
}: any) => (
  <Typography
    {...innerProps}
    className={cn(styles.placeholder, {
      [styles.placeholderLight]: themeCustom === "light",
      [styles.placeholderSearchable]: searchable,
    })}
    variant="bodyM"
  >
    {children}
  </Typography>
);

const ValueContainer = ({ children }: any) => <>{children}</>;

const SingleValue = ({
  innerProps,
  children,
  valueRenderer,
  data,
  searchable,
  themeCustom,
}: any) => {
  if (valueRenderer) {
    return valueRenderer({ data: data.label, innerProps });
  }

  return (
    <Typography
      {...innerProps}
      className={cn(styles.value, {
        [styles.searchableValue]: searchable,
        [styles.valueLight]: themeCustom === "light",
      })}
      variant="bodyM"
    >
      {children}
    </Typography>
  );
};

const MenuList = ({ themeCustom, ...props }: any) => (
  <selectComponents.MenuList
    {...props}
    className={cn(styles.menuList, {
      [styles.menuListLight]: themeCustom === "light",
    })}
  />
);

const Option = ({
  innerProps,
  label,
  children,
  optionRenderer,
  isSelected,
  themeCustom,
}: any) => {
  if (optionRenderer) {
    return optionRenderer({ data: label, innerProps, isSelected });
  }

  return (
    <div
      {...innerProps}
      className={cn(styles.option, {
        [styles.optionLight]: themeCustom === "light",
        [styles.optionSelected]: isSelected,
        [styles.optionSelectedLight]: isSelected && themeCustom === "light",
      })}
    >
      <Typography
        className={styles.optionText}
        variant="bodyM"
        weight="regular"
      >
        {children}
        {/* {isSelected && <CheckIcon className={styles.optionIcon} />} */}
      </Typography>
    </div>
  );
};

const NoOptionsMessage = ({ innerProps }: any) => (
  <Typography {...innerProps} className={styles.noOption}>
    No options
  </Typography>
);

const MultiValueContainer = ({ innerProps, children, themeCustom }: any) => (
  <div
    {...innerProps}
    className={cn(styles.tag, {
      [styles.tagLight]: themeCustom === "light",
    })}
  >
    {children}
  </div>
);

const MultiValueLabel = ({ innerProps, children }: any) => (
  <Typography {...innerProps} className={styles.label} variant="bodyM">
    {children}
  </Typography>
);

const MultiValueRemove = ({ innerProps, themeCustom }) => (
  <div
    {...innerProps}
    className={cn(styles.removeContainer, {
      [styles.removeContainerLight]: themeCustom === "light",
    })}
  >
    <CrossIcon name="cross" className={styles.removeIcon} />
  </div>
);

const SIZES: Sizes = {
  small: styles.small,
  medium: styles.medium,
};

//FIXME: add typings
const Select = ({
  className,
  controlClassName,
  menuClassName,
  withoutBorder,
  ariaLabel,
  loading,
  size = "medium",
  multi = false,
  searchable = false,
  disabled = false,
  mode = "portal",
  type = "default",
  themeCustom = "default",
  placeholder,
  clearText,
  options,
  variant,
  haveError,
  optionRenderer,
  valueRenderer,
  blockScrollOnMenu = true,
  ...restProps
}: any) => {
  const components = {
    Control: (props: any) => (
      <Control
        {...props}
        withoutBorder={withoutBorder}
        controlClassName={controlClassName}
        themeCustom={themeCustom}
        haveError={haveError}
      />
    ),
    DropdownIndicator,
    ClearIndicator: (props: any) => (
      <ClearIndicator {...props} clearText={clearText} />
    ),
    Placeholder: (props) => (
      <Placeholder
        {...props}
        themeCustom={themeCustom}
        searchable={searchable}
      />
    ),
    SingleValue: (valueProps: any) => (
      <SingleValue
        {...valueProps}
        themeCustom={themeCustom}
        searchable={searchable}
        valueRenderer={valueRenderer}
      />
    ),
    MultiValueLabel,
    MultiValueRemove: (props: any) => (
      <MultiValueRemove {...props} themeCustom={themeCustom} />
    ),
    MenuList: (props: any) => <MenuList {...props} themeCustom={themeCustom} />,
    Option: (optionProps: any) => (
      <Option
        {...optionProps}
        themeCustom={themeCustom}
        optionRenderer={optionRenderer}
      />
    ),
    NoOptionsMessage,
    MultiValueContainer: (props: any) => (
      <MultiValueContainer {...props} themeCustom={themeCustom} />
    ),
    ValueContainer,
    IndicatorSeparator: null,
    Menu: (props: any) => (
      <Menu
        {...props}
        menuClassName={menuClassName}
        themeCustom={themeCustom}
      />
    ),
    // Group,
  };

  const commonProps: any = {
    isLoading: loading,
    isMulti: multi,
    isDisabled: disabled,
    isSearchable: searchable,
    closeMenuOnSelect: !multi,
    unstyled: true,
    placeholder,
    components,
  };

  if (type === "default") {
    commonProps.options = loading ? Object.freeze([]) : options;
  }

  if (mode === MODES.default) {
    commonProps.styles = { control: () => null };
  }

  if (mode === MODES.portal || mode === MODES.bodyPortal) {
    commonProps.styles = {
      control: () => null,
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    };
  }

  const SelectComponent = TYPES[type];

  switch (MODES[mode]) {
    case MODES.default:
      return <SelectComponent {...commonProps} {...restProps} />;

    case MODES.portal:
      return (
        <SelectComponent
          {...commonProps}
          {...restProps}
          className={cn(
            styles.container,
            { [styles.multiContainer]: multi },
            SIZES[size],
            className
          )}
          menuPlacement="auto"
          menuPosition="absolute"
          menuShouldBlockScroll={blockScrollOnMenu}
          menuShouldScrollIntoView={false}
          aria-label={ariaLabel || "Select"}
        />
      );

    case MODES.bodyPortal:
      return (
        <SelectComponent
          {...commonProps}
          {...restProps}
          className={cn(
            styles.container,
            { [styles.multiContainer]: multi },
            SIZES[size],
            className
          )}
          menuPortalTarget={
            typeof window !== "undefined" ? document.body : null
          }
          menuPlacement="auto"
          // menuPosition="fixed"
          menuShouldBlockScroll={blockScrollOnMenu}
          aria-label={ariaLabel || "Select"}
        />
      );
  }
};

export default Select;
